import * as React from "react";
import { ErrorMessage, Field, Formik } from "formik";
import classNames from "classnames";
import {
  Currency,
  BackgammonTableInput,
  BackgammonTableType,
} from "../../../../graphql/generated";
import {
  BackgammonDefaultConfigQueryQuery,
  BackgammonDefaultConfigQueryQueryVariables,
} from "./graphql/BackgammonDefaultConfigQuery.generated";
import { loader } from "graphql.macro";
import { useQuery } from "../../../../hooks/useQuery";
import {
  BackgammonCreateOpenTableMutation,
  BackgammonCreateOpenTableMutationVariables,
} from "./graphql/BackgammonCreateOpenTableMutation.generated";
import { useTranslation } from "react-i18next";
import { Loader } from "../../../Loader";
import { useMutation } from "../../../../hooks/useMutation";
import { useCustomChatContext, useUser } from "../../../UserProvider";
import styles from "./CreateTable.module.scss";
import { Input, Slider } from "../../../Slider";
import { useNumberFormatter } from "../../../../hooks/useNumberFormatter";
import { FieldContainer } from "../../common/createtable/FieldContainer";
import { SliderLabel } from "../../common/createtable/SliderLabel";
import { AbsoluteCenter } from "../../../AbsoluteCenter";
import { CombinedError } from "@urql/core/dist/types/utils/error";
import { useIsMounted } from "../../../../hooks/useIsMounted";
import { ButtonWithSound } from "../../../AudioProvider";
import * as Yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faTimes } from "@fortawesome/pro-solid-svg-icons";
import {
  BackgammonCreateTournamentTableMutation,
  BackgammonCreateTournamentTableMutationVariables,
} from "./graphql/BackgammonCreateTournamentTableMutation.generated";
import { SliderInputProps } from "@reach/slider";
import { clamp } from "../../../../utils";
import { MAX_LOSER_MULTIPLIER } from "../Game/types";
import { TableTypeSelect } from "../../common/createtable/TableTypeSelect";
import { useRegisterPopup } from "../../../Register";
import { useGuestId } from "../../../../hooks/useGuestId";

const BACKGAMMON_DEFAULT_CONFIG_QUERY = loader(
  "./graphql/BackgammonDefaultConfigQuery.graphql"
);

const BACKGAMMON_CREATE_OPEN_TABLE_MUTATION = loader(
  "./graphql/BackgammonCreateOpenTableMutation.graphql"
);

const BACKGAMMON_CREATE_TOURNAMENT_TABLE_MUTATION = loader(
  "./graphql/BackgammonCreateTournamentTableMutation.graphql"
);

const CUBE_VALUES = [2, 4, 8, 16, 32, 64];

const PORTES_MAX_ROUND_NUMBER = 5;

const PORTES_DEFAULT_ROUND_NUMBER = 3;

const getMinBetByNumberOfRounds = (
  array: Array<{ maxRounds: number; minBet: number }>,
  numberOfRounds: number
) => {
  const availableRanges = array.filter((e) => e.maxRounds >= numberOfRounds);
  if (availableRanges.length === 0) {
    return 0;
  }
  return availableRanges[0].minBet;
};

const CubeValuesSlider = ({
  value,
  min = 0,
  max = min * CUBE_VALUES[CUBE_VALUES.length - 1],
  step = 1,
  handleAlignment = "contain",
  onChange,
  disabled = false,
  ...rest
}: { value: number; onChange: (value: number) => void } & Omit<
  SliderInputProps,
  "value" | "defaultValue" | "onChange" | "children"
>) => {
  const safeOnChange = (newValue: number) => {
    // Since JavaScript has problems with large floats, round
    // the final value to 5 digits after the decimal point (see #4124)
    return onChange(parseFloat(newValue.toFixed(5)));
  };
  const onButtonChange = (step: number) => () => {
    const isIncreasingStep = step >= 0;
    if (isIncreasingStep) {
      safeOnChange(clamp(value * 2, min, max));
    } else {
      safeOnChange(clamp(value / 2, min, max));
    }
  };

  return (
    <div className="flex items-center w-full">
      <div className="flex-shrink-0 pr-1">
        <ButtonWithSound
          disabled={disabled}
          type="button"
          className="text-sm rounded-full btn btn-green-600 btn-sm"
          onClick={onButtonChange(step * -1)}
        >
          <FontAwesomeIcon icon={faMinus} />
        </ButtonWithSound>
      </div>
      <div className="flex-1 px-1">
        <Input
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={() => {}}
          handleAlignment={handleAlignment}
          disabled={disabled}
          {...rest}
        />
      </div>
      <div className="flex-shrink-0 pl-1">
        <ButtonWithSound
          disabled={disabled}
          type="button"
          className="text-sm rounded-full btn btn-green-600 btn-sm"
          onClick={onButtonChange(step)}
        >
          <FontAwesomeIcon icon={faPlus} />
        </ButtonWithSound>
      </div>
    </div>
  );
};

const BackgammonTableConfigForm = ({
  sectionId,
  config,
  onSubmit,
}: {
  sectionId: string;
  config: NonNullable<
    BackgammonDefaultConfigQueryQuery["backgammonDefaultConfig"]
  >;
  onSubmit: (input: BackgammonTableInput) => Promise<CombinedError | null>;
}) => {
  const [error, setError] = React.useState<string>();
  const isMounted = useIsMounted();
  const { user } = useUser();
  const { t } = useTranslation();
  const numberFormatter = useNumberFormatter();
  const { setOpenedChat } = useCustomChatContext();
  const registration = useRegisterPopup();
  const [, , , getIsLoggedAsGuest] = useGuestId();

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        bet: Yup.number()
          .min(config.bet.minimum)
          .max(config.bet.maximum)
          // eslint-disable-next-line no-template-curly-in-string
          .test("is-within-range", "${path} is not within range", (value) => {
            return (
              value !== null &&
              value !== undefined &&
              value % config.bet.step === 0
            );
          })
          .required(),
      }),
    [config]
  );

  const sortedCommissions = React.useMemo(
    () => config.commissions.sort((a, b) => a.maxBet - b.maxBet),
    [config.commissions]
  );

  const calculatePrize = React.useCallback(
    (betValue: number, numberOfPlayers: number) => {
      for (let i = 0; i < sortedCommissions.length; i++) {
        if (betValue <= sortedCommissions[i].maxBet) {
          return (
            numberOfPlayers *
            betValue *
            (1 - sortedCommissions[i].commission / 100)
          );
        }
      }
      return 0;
    },
    [sortedCommissions]
  );

  const [prize, setPrize] = React.useState<number>();

  React.useEffect(() => {
    setPrize(calculatePrize(config.bet.default, 2));
  }, [config.bet.default, calculatePrize]);

  const isPortes = React.useMemo(() => {
    return !config.usingDoublingCube;
  }, [config.usingDoublingCube]);

  const [roundsNumber, setRoundsNumber] = React.useState(
    isPortes ? PORTES_DEFAULT_ROUND_NUMBER : 1
  );
  const minimumBetValue = getMinBetByNumberOfRounds(
    config.betByNumberOfRounds,
    roundsNumber
  );

  return (
    <Formik
      initialValues={{
        name: user.name,
        turnTime: config.turnTime.default,
        bet: isPortes ? minimumBetValue : config.bet.default,
        type: BackgammonTableType.Public,
        maxRoundNumber: config.usingDoublingCube
          ? 1
          : PORTES_DEFAULT_ROUND_NUMBER,
      }}
      validationSchema={validationSchema}
      validate={(values) => {
        const errors: { [key: string]: string | undefined } = {};
        const availableBalance =
          user.balances.find(
            (balance) => balance.currency === Currency.BlueChips
          )?.value ?? 0;
        let multiplier = 1;
        if (values.bet * multiplier > availableBalance) {
          errors.bet = t("insufficient_funds");
        }
        return errors;
      }}
      onSubmit={(values) => {
        setOpenedChat(false);
        return onSubmit({
          sectionId,
          ...values,
        }).then((error) => {
          if (error != null && isMounted.current) {
            if (error.graphQLErrors.length > 0)
              setError(error.graphQLErrors[0].message);
            else if (error.networkError) setError(error.networkError.message);
          }
        });
      }}
    >
      {({
        values,
        isValid,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleSubmit,
      }) => {
        return (
          <form onSubmit={handleSubmit} className="flex flex-col h-full">
            {error && (
              <div className="flex-shrink-0 p-4 mb-4 text-center bg-red-700 rounded">
                <span className="block font-bold text-white">{error}</span>
              </div>
            )}
            <div
              className={classNames(
                "flex-1",
                "overflow-auto",
                styles.scrollable
              )}
            >
              <FieldContainer name={t("name")}>
                <Field
                  name="name"
                  className="block w-full py-1 rounded form-input"
                />
              </FieldContainer>
              <FieldContainer name={t("turn_time")}>
                <Slider
                  disabled={isSubmitting}
                  value={values.turnTime}
                  min={config.turnTime.minimum}
                  max={config.turnTime.maximum}
                  step={config.turnTime.step}
                  onChange={(value) => {
                    setFieldTouched("turnTime", true);
                    setFieldValue("turnTime", value);
                  }}
                />
                <SliderLabel
                  value={t("seconds_value", {
                    value: numberFormatter.format(values.turnTime),
                  })}
                />
              </FieldContainer>
              <FieldContainer name={t("bet")}>
                <Slider
                  disabled={isSubmitting}
                  value={
                    typeof values.bet !== "number"
                      ? isPortes
                        ? minimumBetValue
                        : config.bet.minimum
                      : values.bet
                  }
                  min={isPortes ? minimumBetValue : config.bet.minimum}
                  max={config.bet.maximum}
                  step={config.bet.step}
                  onChange={(value) => {
                    setFieldTouched("bet", true);
                    setFieldValue("bet", value);
                    setPrize(calculatePrize(value, 2));
                  }}
                />
                {isPortes &&
                  values.bet < minimumBetValue &&
                  setFieldValue("bet", minimumBetValue)}
                <Field
                  disabled={isSubmitting}
                  name="bet"
                  type="number"
                  className="w-2/5 py-0 text-sm text-center rounded shadow-xl form-input"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = Number(e.target.value);
                    if (
                      newValue >= config.bet.minimum &&
                      newValue <= config.bet.maximum
                    ) {
                      setFieldTouched("bet", true);
                      setFieldValue("bet", newValue);
                      setPrize(calculatePrize(newValue, 2));
                    }
                  }}
                />
                <ErrorMessage
                  name="bet"
                  className="mt-2 font-bold text-red-600"
                  component="div"
                />
              </FieldContainer>
              <FieldContainer name={t("prize")}>
                <div className="w-2/5 py-0 text-sm text-center rounded shadow-xl form-input">
                  {prize}
                </div>
              </FieldContainer>
              {config.usingDoublingCube && (
                <FieldContainer name={t("max_win")}>
                  <div className="w-2/5 py-0 text-sm text-center rounded shadow-xl form-input">
                    {values.bet *
                      MAX_LOSER_MULTIPLIER *
                      config.maxAllowedCubeValue}
                  </div>
                </FieldContainer>
              )}
              {isPortes && (
                <FieldContainer name={t("number_of_rounds")}>
                  <Slider
                    disabled={isSubmitting}
                    value={
                      typeof values.maxRoundNumber !== "number"
                        ? PORTES_DEFAULT_ROUND_NUMBER
                        : values.maxRoundNumber
                    }
                    min={1}
                    max={PORTES_MAX_ROUND_NUMBER}
                    step={1}
                    onChange={(value) => {
                      setFieldTouched("maxRoundNumber", true);
                      setFieldValue("maxRoundNumber", value);
                      setRoundsNumber(value);
                    }}
                  />
                  <Field
                    disabled={isSubmitting}
                    name="maxRoundNumber"
                    type="number"
                    className="w-2/5 py-0 text-sm text-center rounded shadow-xl form-input"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newValue = Number(e.target.value);
                      if (
                        newValue >= 1 &&
                        newValue <= PORTES_MAX_ROUND_NUMBER
                      ) {
                        setFieldTouched("maxRoundNumber", true);
                        setFieldValue("maxRoundNumber", newValue);
                      }
                    }}
                  />
                  <ErrorMessage
                    name="maxRoundNumber"
                    className="mt-2 font-bold text-red-600"
                    component="div"
                  />
                </FieldContainer>
              )}
              <div className="w-full px-2">
                <FieldContainer name={t("table_type")}>
                  <TableTypeSelect
                    disabled={isSubmitting}
                    values={BackgammonTableType}
                    value={values.type}
                    onChange={(value) => {
                      if (
                        getIsLoggedAsGuest() &&
                        value.toUpperCase() === "PRIVATE"
                      )
                        registration.openRegisterPopup();
                      else {
                        setFieldTouched("type", true);
                        setFieldValue("type", value);
                      }
                    }}
                  />
                </FieldContainer>
              </div>
            </div>
            <FieldContainer className="mt-4">
              <label className="inline-flex items-center justify-between w-full">
                <span className="text-white">
                  {t("backgammon.doubles_number")}
                </span>
              </label>
              <div className="mt-2 text-left text-white">
                {t("backgammon.amount_of_chips")}
              </div>
            </FieldContainer>
            <ButtonWithSound
              type="submit"
              disabled={isSubmitting || !isValid}
              className="flex-shrink-0 w-full mt-4 rounded btn btn-blue-600"
            >
              {!isSubmitting ? t("create_table") : t("loading")}
            </ButtonWithSound>
          </form>
        );
      }}
    </Formik>
  );
};

function CreateOpenTable({
  sectionId,
  onTableCreated,
  onDismiss,
}: {
  sectionId: string;
  onTableCreated: (id: string) => void;
  onDismiss: () => void;
}) {
  const [{ data }] = useQuery<
    BackgammonDefaultConfigQueryQuery,
    BackgammonDefaultConfigQueryQueryVariables
  >({
    query: BACKGAMMON_DEFAULT_CONFIG_QUERY,
    variables: {
      sectionId,
    },
  });
  const [, createTable] = useMutation<
    BackgammonCreateOpenTableMutation,
    BackgammonCreateOpenTableMutationVariables
  >(BACKGAMMON_CREATE_OPEN_TABLE_MUTATION, false);

  const { t } = useTranslation();

  const config = data?.backgammonDefaultConfig;
  if (config === null)
    throw new Error("Game backgammon not supported on this room");

  return (
    <>
      <div
        className="absolute top-0 bottom-0 left-0 right-0 z-20 bg-black-alpha-30"
        onClick={onDismiss}
      />
      <div
        className="absolute top-0 bottom-0 right-0 z-30 w-1/3 p-4 bg-blue-800 shadow-2xl"
        aria-label={t("create_table")}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between flex-shrink-0 mb-4 -mx-2 text-white">
            <span className="px-2 text-lg font-bold truncate">
              {t("create_table")}
            </span>
            <div className="flex-shrink-0 px-2">
              <button onClick={onDismiss}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
          <div className="relative flex-1 overflow-hidden">
            {config === undefined ? (
              <AbsoluteCenter>
                <Loader description={t("loading_game_config")} />
              </AbsoluteCenter>
            ) : (
              <BackgammonTableConfigForm
                sectionId={sectionId}
                config={config}
                onSubmit={(input) =>
                  createTable({ input }).then((result) => {
                    if (result.error) {
                      return result.error;
                    } else {
                      onTableCreated(
                        result!.data!.backgammonCreateOpenTable!.id!
                      );
                      return null;
                    }
                  })
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function CreateTournamentTable({
  onTableCreated,
  sectionId,
  onDismiss,
}: {
  sectionId: string;
  onTableCreated: (id: string) => void;
  onDismiss: () => void;
}) {
  const [, createTable] = useMutation<
    BackgammonCreateTournamentTableMutation,
    BackgammonCreateTournamentTableMutationVariables
  >(BACKGAMMON_CREATE_TOURNAMENT_TABLE_MUTATION, false);

  const isMounted = useIsMounted();
  React.useEffect(() => {
    createTable({ sectionId: sectionId }).then((result) => {
      if (result.error) {
        if (isMounted.current) onDismiss();
      } else onTableCreated(result!.data!.backgammonCreateTournamentTable!.id!);
    });
  }, [sectionId, createTable, onTableCreated, isMounted, onDismiss]);
  return (
    <>
      <div className="absolute top-0 bottom-0 left-0 right-0 z-20 bg-black-alpha-30" />
      <AbsoluteCenter>
        <Loader description="Creating table" />
      </AbsoluteCenter>
    </>
  );
}

export const CreateTable = ({
  isTournament,
  ...rest
}: {
  sectionId: string;
  isTournament: boolean;
  onTableCreated: (id: string) => void;
  onDismiss: () => void;
}) => {
  if (!isTournament) return <CreateOpenTable {...rest} />;
  else return <CreateTournamentTable {...rest} />;
};
